import React from 'react';

import {
  type AmsBrowsePageFieldsFragment,
  useAmsBrowseAccountsQuery,
  useAmsBrowseCertificatesQuery,
  useAmsBrowseFilesQuery,
  useAmsBrowsePoliciesQuery,
  useAmsBrowsePolicySearchQuery
} from '~/api/graphql';
import { useDebouncedValue } from '~/hooks/use-list-controls';
import {
  type Account,
  ACCOUNT_PAGE_SIZE,
  type Capability,
  type CertificateFilters,
  FILE_PAGE_SIZE,
  type FileFilters,
  type PolicyFilters
} from './ams-browser-model';
import { type AmsRef, amsRefInput } from './ams-ref';

export type BrowseList<T> = {
  items: T[];
  /** How many records the list holds in all, where the read could count them. */
  total?: number | null;
  initialLoading: boolean;
  failed: boolean;
  /** Whether loadMore has a further page to fetch. */
  more: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  loadMoreFailed: boolean;
};

type FetchMoreResult = { error?: unknown; errors?: ReadonlyArray<unknown> };

type ListQuery<I> = {
  loading: boolean;
  error?: unknown;
  fetchMore: (options: { variables: { input: I } }) => Promise<FetchMoreResult>;
};

/**
 * A list whose query merges pages by `input.after` (see amsBrowseKeyArgs).
 * Pages load on request rather than on scroll: a list paging itself in would
 * push what follows it away while the user scrolls.
 */
function useBrowseList<T, I extends object>(
  query: ListQuery<I>,
  slice: { items: T[]; page: AmsBrowsePageFieldsFragment } | undefined,
  input: I
): BrowseList<T> {
  // The Load more state belongs to one input, so a failure on one search does
  // not show under the next.
  const inputKey = JSON.stringify(input);
  const [paging, setPaging] = React.useState({ key: '', loading: false, failed: false });
  const current = paging.key === inputKey ? paging : { loading: false, failed: false };
  const next = slice?.page.nextCursor ?? undefined;

  const loadMore = () => {
    if (!next || current.loading) return;
    setPaging({ key: inputKey, loading: true, failed: false });
    const finish = (failed: boolean) =>
      setPaging((p) => (p.key === inputKey ? { key: inputKey, loading: false, failed } : p));
    // The client's errorPolicy 'all' resolves a GraphQL error rather than
    // rejecting, so both paths count as a failure.
    query
      .fetchMore({ variables: { input: { ...input, after: next } } })
      .then((result) => finish(!!result.error || !!result.errors?.length))
      .catch(() => finish(true));
  };

  return {
    items: slice?.items ?? [],
    total: slice?.page.total,
    initialLoading: query.loading && !slice,
    failed: !!query.error && !slice,
    more: !!next,
    loadMore,
    loadingMore: current.loading,
    loadMoreFailed: current.failed
  };
}

// Going back to a view reads its lists from the cache, keeping the pages
// Load more added; a changed filter is a new input and reads afresh.
const fetchPolicy = 'cache-first';

export function useAccountSearch(capability: Capability, query: string) {
  const input = { integrationId: capability.integrationId, query, limit: ACCOUNT_PAGE_SIZE };
  const result = useAmsBrowseAccountsQuery({ variables: { input }, fetchPolicy });
  const data = result.data?.amsBrowseAccounts;
  return useBrowseList(result, data && { items: data.accounts, page: data.page }, input);
}

export function usePolicySearch(capability: Capability, query: string) {
  const input = { integrationId: capability.integrationId, query, limit: ACCOUNT_PAGE_SIZE };
  const result = useAmsBrowsePolicySearchQuery({ variables: { input }, fetchPolicy });
  const data = result.data?.amsBrowsePolicySearch;
  return useBrowseList(result, data && { items: data.matches, page: data.page }, input);
}

export function usePolicyList(account: Account, filters: PolicyFilters) {
  const query = useDebouncedValue(filters.query.trim(), 300);
  const input = {
    account: amsRefInput(account.ref),
    query: query || undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    expiresFrom: filters.expires.from,
    expiresTo: filters.expires.to,
    limit: ACCOUNT_PAGE_SIZE
  };
  const result = useAmsBrowsePoliciesQuery({ variables: { input }, fetchPolicy });
  const data = result.data?.amsBrowsePolicies;
  return useBrowseList(result, data && { items: data.policies, page: data.page }, input);
}

export function useCertificateList(account: Account, filters: CertificateFilters, skip: boolean) {
  const query = useDebouncedValue(filters.query.trim(), 300);
  const input = {
    account: amsRefInput(account.ref),
    query: query || undefined,
    issuedFrom: filters.issued.from,
    issuedTo: filters.issued.to,
    limit: ACCOUNT_PAGE_SIZE
  };
  const result = useAmsBrowseCertificatesQuery({ variables: { input }, skip, fetchPolicy });
  const data = result.data?.amsBrowseCertificates;
  return useBrowseList(result, data && { items: data.certificates, page: data.page }, input);
}

export function useFileList(scope: AmsRef, filters: FileFilters, skip: boolean) {
  const title = useDebouncedValue(filters.query.trim(), 300);
  const input = {
    scope: amsRefInput(scope),
    title: title || undefined,
    attachedFrom: filters.attached.from,
    attachedTo: filters.attached.to,
    types: filters.types.length > 0 ? filters.types : undefined,
    folder: filters.folder.length > 0 ? filters.folder.join('/') : undefined,
    limit: FILE_PAGE_SIZE
  };
  const result = useAmsBrowseFilesQuery({ variables: { input }, skip, fetchPolicy });
  const data = result.data?.amsBrowseFiles;
  return useBrowseList(result, data && { items: data.files, page: data.page }, input);
}

/** A tab's counter: the list's total where the read could count it. */
export function countOf(list: BrowseList<unknown>): string | undefined {
  if (list.initialLoading || list.failed) return undefined;
  if (list.total != null) return String(list.total);
  return `${list.items.length}${list.more ? '+' : ''}`;
}

/**
 * The count bar under a list while more of it remains to load. A list loaded
 * whole has none, since the tab's counter already says how long it is.
 */
export function frameLabel(list: BrowseList<unknown>, noun: string): string | null {
  const loaded = list.items.length;
  if (list.total != null && list.total > loaded) return `${loaded} of ${list.total} ${noun}`;
  return list.more ? `${loaded}+ ${noun}` : null;
}
